import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, FormControl, Grid, IconButton, InputAdornment, InputLabel, OutlinedInput, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useCreateVoucherImageMutation, usePaymentInitTransactionMutation } from "../../../store/api";
import { AttachFile } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/types/rootState";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useNavigate } from "react-router-dom";
import { getPrimaryColorByAccountId, getThemeByAccountId, styleConstants } from "../../../utils/styleConstants";
import { BankAccountItem } from "../../../constants/bankAccounts";
import { IPaymentInitTransaction } from "../../../store/interfaces/IPaymentInitTransaction";
import { IPaymentInitTransactionResponse } from "../../../store/interfaces/IPaymentInitTransactionResponse";
interface TransferPaymentMethodProps {
  purchaserData: any;
  selectedPaymentMethod: string;
  raffleId: string;
  ticketQuantity: number;
  selectedTickets: number[] | undefined;
  totalAmount: number;
  totalUSDAmount: number;
  minPurchase: number;
  setOpen: (open: boolean) => void;
  selectedBankAccount?: BankAccountItem | undefined;
}

export const TransferPaymentMethod: React.FC<TransferPaymentMethodProps> = ({
  purchaserData,
  selectedPaymentMethod,
  raffleId,
  ticketQuantity,
  selectedTickets,
  totalAmount,
  totalUSDAmount,
  minPurchase,
  selectedBankAccount,
}: TransferPaymentMethodProps) => {

  const { enqueueSnackbar } = useSnackbar();
  const accountId = useSelector(({ account }: RootState) => account.id);
  const accountInformation = useSelector(({ account }: RootState) => account);
  const raffleType = useSelector(({ raffle }: RootState) => raffle.type);
  const hasReferenceNumber = useSelector(({ raffle }: RootState) => raffle.hasReferenceNumber);
  const isPremiumRaffle = useSelector(({ raffle }: RootState) => raffle.isPremium);
  const raffleTemplateId = useSelector(({ raffle }: RootState) => raffle.templateId);

  const [voucherImages, setVoucherImages] = useState<File[]>([]);

  const [counter, setCounter] = useState(1);
  
  const redirectToWhatsapp = (paymentInitResponse: IPaymentInitTransactionResponse) => {
    const purchaserEmail = paymentInitResponse.purchaser.email;
    const purchaserName = paymentInitResponse.purchaser.name;
  
    const whatsappNumber = accountInformation.socialMediaList.find(sm => sm.name === 'supportWhatsapp')?.username;
    const message = `Hola, soy ${purchaserName}. Compré ${ticketQuantity} números en la rifa de ${accountInformation.name}. En el siguiente link puedo revisar la información y el estado de mi compra: ${accountInformation.url}/self-verification/${purchaserEmail}`;
    const whatsappUrl = `https://wa.me/${whatsappNumber}/?text=${encodeURIComponent(message)}`;
  
    window.location.href = whatsappUrl;
  }
  
  const [initTransaction, { isLoading: isLoadingInitTransaction, data: dataInitTransaction }] = usePaymentInitTransactionMutation();
  const [createVoucher, { isLoading: isLoadingCreateVoucher, data: voucherSaved }] = useCreateVoucherImageMutation();


  const handleImageChange = (files: FileList | null) => {
    if (!files) return;
    const voucher = Array.from(files).slice(0, 1);
    setVoucherImages(voucher);
  };

  const removeVoucherImage = () => {
    setVoucherImages([]);
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (voucherSaved === true && isPremiumRaffle && dataInitTransaction) {
      interval = setInterval(() => {
        setCounter((prev) => {
          if (prev === 1) {
            clearInterval(interval);
            redirectToWhatsapp(dataInitTransaction);
          }
          return prev - 1;
        });
      }, 1000);
    }
  
    return () => clearInterval(interval);
  }, [dataInitTransaction, voucherSaved]);


  const navigate = useNavigate();

  const handleInitTransaction = async () => {
    try {


      if (!purchaserData.name || !purchaserData.email || !purchaserData.phone || (hasReferenceNumber && !purchaserData.referenceNumber)) {
        enqueueSnackbar("Ingrese todos los datos de contacto.", {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "top",
          },
        });
        return;
      }

      if (!selectedPaymentMethod) {
        return;
      }

      const response = await initTransaction({
        raffleId,
        conf: {
          referenceNumber: purchaserData.referenceNumber,
          ticketQuantity,
          paymentMethodId: selectedPaymentMethod,
          purchaser: purchaserData,
          selectedTickets,
          targetBankAccount: {
            id: selectedBankAccount?.bankId || 'Banco no identificado',
            name: selectedBankAccount?.name || 'Nombre no identificado',
            accountNumber: selectedBankAccount?.accountNumber || 'Cuenta no identidiaca'
          }
        },
      }).unwrap();

      if (
        response.status === "TRANSFER_PAYMENT_CREATED" &&
        selectedPaymentMethod === "transfer"
      ) {
        if (accountId === "super-rifas-latam") {
            navigate("/successPurchase")
        } else {
         createVoucher({
          accountId: accountId,
          imageFile: voucherImages[0],
          imageName: response.purchaseOrderId,
          raffleId: raffleId,
        }).then(() => {
          enqueueSnackbar(response.description, {
            variant: "success",
            anchorOrigin: {
              horizontal: "right",
              vertical: "top",
            },
          });
          if (!isPremiumRaffle) {
            navigate("/successPurchase")
          }
        }).catch((error: any)  => {
          enqueueSnackbar(error, {
            variant: "error",
            anchorOrigin: {
              horizontal: "right",
              vertical: "top",
            },
          });
        }); 
        }

        return;
      }

      if (response.status === "LIMIT_EXCEEDED" || response.status === "RAFFLE_NO_LONGER_AVAILABLE") {
        enqueueSnackbar(response.error, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "top",
          },
        });
      } else {
        console.log("response.status =>", response.status)
        enqueueSnackbar("Error al momento de ir al pago!", {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "top",
          },
        });
      }
    } catch (error) {
      console.log("response.error =>", error)
      enqueueSnackbar("Error al momento de ir al pago!", {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
    }
  };

  interface LoadingComponentProps {
    message: string;
  }
  const LoadingComponent = ({ message }: LoadingComponentProps) => (
    <Box
      m={3}
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
      justifyContent="center"
    >
      <Typography 
      variant="h6"
      textAlign={"center"}
      fontWeight={"bold"}>{message}</Typography>
      <CircularProgress
        size={40}
        variant="indeterminate"
      />
    </Box>
  );

  return (
      <Grid container>
        {(!dataInitTransaction ||
          dataInitTransaction.status === "UNAUTHORIZED_FOR_GENERATE_TICKETS") &&
          !isLoadingInitTransaction && (
            <Grid
              item
              sx={{
                width: "100%",
              }}
            >
              {accountId !== 'super-rifas-latam' && <Button variant="text" component="label" startIcon={<AttachFile></AttachFile>} sx={{
                border: "inherit"
              }}>
                    {raffleType === 'raffle' || raffleType === 'lottery' ? `Click AQUÍ para subir comprobante de pago ${selectedBankAccount && `${selectedBankAccount.bank} - ${selectedBankAccount && selectedBankAccount?.ticketPriceAmount * ticketQuantity}$`}` : 'Subir screenshot de tu perfil de instagram'}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => handleImageChange(e.target.files)}
                  key={`voucher-input-button`}
                />
              </Button>}
              <Box>
                <Typography fontWeight={"bold"} display={"inline"}>
                  
                </Typography>
              </Box>

              {voucherImages.map((image, index) => (
                <Box  key={`image-preview-${index}`}>
                <img src={URL.createObjectURL(image)} width={"100%"} />
                <Box display={"flex"} justifyContent={"center"}>
                  <IconButton onClick={() => removeVoucherImage()}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
              ))}

              {/* <div style={{
                 color:"white"
              }}>voucherImages.length === 0: {JSON.stringify(voucherImages.length === 0)}</div>
              <div style={{
                 color:"white"
              }}>!selectedPaymentMethod: {JSON.stringify(!selectedPaymentMethod)}</div>
              <div style={{
                 color:"white"
              }}>isLoadingInitTransaction: {JSON.stringify(isLoadingInitTransaction)}</div>
              <div style={{
                 color:"white"
              }}>totalUSDAmount === 0 : {JSON.stringify(totalUSDAmount === 0 )}</div>
              <div style={{
                 color:"white"
              }}>ticketQuantity === 0: {JSON.stringify(ticketQuantity === 0)}</div>
              <div style={{
                 color:"white"
              }}>ticketQuantity minPurchase: {JSON.stringify(ticketQuantity < minPurchase)}</div> */}

              {/* <Alert variant="outlined" severity="error">La compra mínima es: <b>{minPurchase}</b> tickets!</Alert> */}

              <Button
                disabled={
                  (voucherImages.length === 0 && accountId !== 'super-rifas-latam') ||
                  !selectedPaymentMethod ||
                  isLoadingInitTransaction ||
                  totalUSDAmount === 0 || 
                  ticketQuantity === 0 ||
                  ticketQuantity < minPurchase
                }
                fullWidth
                type="submit"
                color="primary"
                sx={{
                  opacity: (voucherImages.length === 0 && accountId !== 'super-rifas-latam') || totalUSDAmount === 0 || ticketQuantity === 0 || ticketQuantity < minPurchase ? 0.3 : 1,
                  backgroundColor: `${getPrimaryColorByAccountId(accountId)} !important`,
                  borderColor: "#000",
                  mt: 2,
                }}
                variant="contained"
                onClick={handleInitTransaction}
              >
                {!isLoadingInitTransaction ? (
                  <>
                    {/* <Typography color={"#000"} ml={1} fontWeight={"bold"}>
                      Comprar tickets{" "}
                      {selectedPaymentMethod && `${totalAmount}$ pesos`}
                    </Typography> */}
                    <Typography color={`${getThemeByAccountId(accountId) === 'dark' ? '#000 !important' : '#FFF !important' }`} ml={1} fontWeight={"bold"}>
                      {raffleType === 'raffle' || raffleType === 'lottery' ? 'Comprar tickets' : 'Participar ahora'}
                    </Typography>
                  </>
                ) : (
                  <CircularProgress size="2rem" />
                )}
              </Button>
            </Grid>
          )}

        {isLoadingInitTransaction && (<LoadingComponent message="Creando orden de compra" />)}
        {voucherSaved && dataInitTransaction && dataInitTransaction.status === 'TRANSFER_PAYMENT_CREATED' && (
          <Box textAlign="center" mt={2}>
            <Typography variant="h6" mb={1}>
              Redirigiendo a WhatsApp en {counter} segundos...
            </Typography>
            <Button 
              variant="outlined" 
              color="success" 
              onClick={() => redirectToWhatsapp(dataInitTransaction)}
            >
              Redirigirme ahora
            </Button>
            <Box mt={2}>
              <Alert severity="info">
                Si no ocurre la redirección automáticamente, por favor asegúrese de tener habilitados los pop-ups en su navegador.
              </Alert>
            </Box>
          </Box>
        )}

        {isLoadingCreateVoucher && (<LoadingComponent message="Guardando comprobante, no recargue la pestaña" />)}

        
      </Grid>
  );
};

